import * as React from 'react';
import {Mutation} from 'react-apollo';
import {Link as RoutedLink, withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import classnames from 'classnames';
import gql from 'graphql-tag';

import {ActionableHeader} from 'shells/actionable-header/actionable-header';
import {colors} from 'shells/colors/colors';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
// @ts-expect-error - imported from .js/.jsx file
import {CopyRegularIcon} from 'shells/icon/svg/copy-regular-icon';
import {LightbulbRegularIcon} from 'shells/icon/svg/lightbulb-regular-icon';
import {PenRulerRegularIcon} from 'shells/icon/svg/pen-ruler-regular-icon';
import {PencilOutlineIcon} from 'shells/icon/svg/pencil-outline-icon';
// @ts-expect-error - imported from .js/.jsx file
import {PencilRegularRenameIcon} from 'shells/icon/svg/pencil-regular-rename-icon';
// @ts-expect-error - imported from .js/.jsx file
import {RetryRegularIcon} from 'shells/icon/svg/retry-regular-icon';
import {StarRegularIcon} from 'shells/icon/svg/star-regular-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {Link} from 'shells/link/link';

import type {
    DeleteStagesetInputFieldInput,
    DeleteStageset as DeleteStagesetMutation,
    DeleteStagesetVariables as DeleteStagesetMutationVariables,
    RestoreStagesetInputFieldInput,
    RestoreStageset as RestoreStagesetMutation,
    RestoreStagesetVariables as RestoreStagesetMutationVariables,
    SetDefaultStagesetInputFieldInput,
    SetDefaultStageset as SetDefaultStagesetMutation,
    SetDefaultStagesetVariables as SetDefaultStagesetMutationVariables,
    StagesetsForPipelineList as StagesetsForPipelineListQuery,
    StagesetBasic as StagesetBasicFragment,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {useGetLimitsByType} from '../../limits';
import {OpenableModals, useOpenableModal} from '../../openable-modals';

import STAGESET_BASIC from '../graphql/fragments/stageset-basic.graphql';
import {useStagesetDeactivate} from '../graphql/hooks/use-stageset-deactivate';
import SET_DEFAULT_STAGESET from '../graphql/mutations/set-default-stageset.graphql';
import GET_STAGESETS from '../graphql/queries/get-stagesets-for-pipeline-list.graphql';
import {StageChevronContainer} from '../stage-chevron-container';

import {ActivateDraftPipelineConfirmationModal} from './activate-draft-pipeline-confirmation-modal';
import {EmptyStagesGraphic} from './empty-stages-graphic';
import {getPipelineStatusBadge, getLeadsListLink} from './helpers';
import {LeadDistributionEditor} from './lead-distribution-editor';
import {PipelineDuplicatePopup} from './pipeline-duplicate-popup';
import {PipelineNamePopup} from './pipeline-name-popup';

import styles from './pipeline-list-item.module.css';

type OwnProps = {
    shouldPreventDragging?: boolean;
    stageset: StagesetBasicFragment;
};

type Props = OwnProps &
    ContextRouter & {
        deleteStageset: (arg1: DeleteStagesetInputFieldInput) => Promise<any>;
        setAsDefault: (arg1: SetDefaultStagesetInputFieldInput) => Promise<any>;
        restoreStageset: (arg1: RestoreStagesetInputFieldInput) => Promise<any>;
    };

export function PipelineListItemComponent(props: Props) {
    const {onOpen: openFeatureGateModal} = useOpenableModal(
        OpenableModals.PIPELINES_FEATURE_GATE_MODAL
    );
    const {limits} = useGetLimitsByType('PIPELINES');
    const [cannotArchiveMessage, setCannotArchiveMessage] = React.useState<
        string | null | undefined
    >(null);
    const [cannotDeactivateMessage, setCannotDeactivateMessage] = React.useState<
        string | null | undefined
    >(null);
    const [isAttemptingToArchivePipeline, setIsAttemptingToArchivePipeline] =
        React.useState<boolean>(false);
    const [isAttemptingToDeactivatePipeline, setIsAttemptingToDeactivatePipeline] =
        React.useState<boolean>(false);
    const [displayRenameAlert, setDisplayRenameAlert] = React.useState<boolean>(false);
    const [isEditingDistributionRules, setIsEditingDistributionRules] =
        React.useState<boolean>(false);
    const [displayDuplicatePopup, setDisplayDuplicatePopup] = React.useState<boolean>(false);
    const [displayActivatePipelineModal, setDisplayActivatePipelineModal] =
        React.useState<boolean>(false);

    const {stagesetDeactivate} = useStagesetDeactivate();

    const handleDuplicatePipeline = React.useCallback(() => {
        if ((limits && limits.canAdd) || NutClientConfig.hasDraftPipelinesFF) {
            setDisplayDuplicatePopup(true);
        } else {
            openFeatureGateModal();
        }
    }, [limits, setDisplayDuplicatePopup, openFeatureGateModal]);

    const handleSetAsDefault = () => {
        props.setAsDefault({
            id: props.stageset.id,
        });
    };

    const handleEditPipeline = () => {
        appHistory.push(props.stageset.htmlUrlPath);
    };

    const handleDuplicatePopupClose = () => {
        setDisplayDuplicatePopup(false);
    };

    const handleDuplicatePopupSuccess = () => {
        handleDuplicatePopupClose();
        if (props.history) {
            props.history.replace({
                pathname: props.history.location.pathname,
                search: `?tab=draft`,
            });
        }
    };

    const handleRenamePipeline = () => {
        setDisplayRenameAlert(true);
    };

    const handleRenamePopupClose = (e?: React.SyntheticEvent<any>) => {
        if (e) e.stopPropagation();

        setDisplayRenameAlert(false);
    };

    const onArchiveAcknowledge = () => {
        setCannotArchiveMessage(null);
    };

    const onDeactivateAcknowledge = () => {
        setCannotDeactivateMessage(null);
    };

    const handleArchivePipeline = () => {
        // If this is defined in the if statement, flow does not check it
        // Fixed in 0.68: https://github.com/facebook/flow/releases/tag/v0.68.0
        const canDelete = props.stageset.canDelete;

        if (canDelete) {
            setIsAttemptingToArchivePipeline(true);
        } else {
            setCannotArchiveMessage(
                'There are open or pending leads in this pipeline. You can close those leads or move them to a different pipeline to archive this one.'
            );
        }
    };

    const handleRestorePipeline = () => {
        props
            .restoreStageset({
                id: props.stageset.id,
                // Now we need to refetch the stagesets so the rest of the app knows
            })
            .then(() => Nut.managedCollections.Stagesets.fetch());
    };

    const onArchiveConfirm = () => {
        props
            .deleteStageset({
                id: props.stageset.id,
                // Now we need to refetch the stagesets so the rest of the app knows
                // We need reset to be true here so it picks up the removals
            })
            .then(() => Nut.managedCollections.Stagesets.fetch({reset: true}));

        setIsAttemptingToArchivePipeline(false);
    };

    const onArchiveCancel = () => {
        setIsAttemptingToArchivePipeline(false);
    };

    const handleOpenActivatePipelineModal = () => {
        if (limits && limits.canAdd) {
            setDisplayActivatePipelineModal(true);
        } else {
            openFeatureGateModal();
        }
    };

    const onDeactivateCancel = () => {
        setIsAttemptingToDeactivatePipeline(false);
    };

    const onDeactivateConfirm = () => {
        stagesetDeactivate({
            stagesetId: props.stageset.id,
        }).then(() => {
            Nut.managedCollections.Stagesets.fetch({reset: true});
        });

        setIsAttemptingToDeactivatePipeline(false);
    };

    const handleDeactivatePipeline = () => {
        const canDeactivate = props.stageset.canDelete;

        if (canDeactivate) {
            setIsAttemptingToDeactivatePipeline(true);
        } else {
            setCannotDeactivateMessage(
                'There are open or pending leads in this pipeline. You can close those leads or move them to a different pipeline to deactivate this one.'
            );
        }
    };

    const {isArchived, default: isDefault} = props.stageset;
    const openLeadCount =
        props.stageset.numOfOpenLeads != null ? props.stageset.numOfOpenLeads : '--';
    const openLeadText = props.stageset.numOfOpenLeads === 1 ? 'open lead' : 'open leads';

    const containerStyle = classnames(styles.container, {
        [styles['container-archived']]: isArchived,
        [styles['container-draggable']]: !props.shouldPreventDragging,
    });

    const stagesContainerStyle = classnames(styles['stages-container'], {
        [styles['stages-container-archived']]: isArchived,
    });

    const stageItems = props.stageset.stages.length ? (
        // @ts-expect-error
        props.stageset.stages.map((stage, index) => {
            return (
                <StageChevronContainer
                    key={stage.id}
                    stage={stage}
                    customStyles={{
                        marginRight: 16,
                        flexGrow: 1,
                        backgroundColor: '#F7F7F7',
                        height: 50,
                        maxWidth: 240,
                    }}
                    hoverStyle={{
                        backgroundColor: colors.offWhiteDk,
                    }}
                    // @ts-expect-error
                    zIndex={props.stageset.stages.length - index}
                    isFirstChild={index === 0}
                    isLastChild={false}
                />
            );
        })
    ) : (
        <EmptyStagesGraphic />
    );

    const getActionMenuOptions = () => {
        const isStagesetArchived = props.stageset.isArchived;
        const isStagesetDefault = props.stageset.default;
        const isStagesetDraft =
            props.stageset.status === 'DRAFT' || props.stageset.status === 'RETIRED';
        const validOptions = [];

        if (!isStagesetArchived) {
            validOptions.push({
                label: 'Edit',
                icon: <PencilOutlineIcon size={16} />,
                handleSelect: handleEditPipeline,
            });

            validOptions.push({
                label: 'Rename',
                icon: <PencilRegularRenameIcon size={16} />,
                handleSelect: handleRenamePipeline,
            });
        } else {
            validOptions.push({
                label: `Restore ${NutClientConfig.hasDraftPipelinesFF ? 'to draft' : ''}`,
                icon: <RetryRegularIcon size={16} />,
                handleSelect: handleRestorePipeline,
            });
        }

        validOptions.push({
            label: 'Make a copy',
            icon: <CopyRegularIcon size={16} />,
            handleSelect: handleDuplicatePipeline,
        });

        if (isStagesetDraft && !isStagesetArchived && NutClientConfig.hasDraftPipelinesFF) {
            validOptions.push({
                label: 'Activate pipeline',
                icon: <LightbulbRegularIcon size={16} wrapWithDiv={false} />,
                handleSelect: handleOpenActivatePipelineModal,
            });
        }

        if (!isStagesetDefault && !isStagesetArchived) {
            if (!NutClientConfig.hasDraftPipelinesFF || !isStagesetDraft) {
                validOptions.push({
                    label: 'Set as default',
                    icon: <StarRegularIcon size={16} />,
                    handleSelect: handleSetAsDefault,
                });
            }
            if (NutClientConfig.hasDraftPipelinesFF && !isStagesetDraft) {
                validOptions.push({
                    label: 'Deactivate pipeline',
                    icon: <PenRulerRegularIcon size={16} />,
                    handleSelect: handleDeactivatePipeline,
                });
            }

            validOptions.push({
                label: 'Archive',
                icon: <TrashRegularIcon size={16} />,
                handleSelect: handleArchivePipeline,
            });
        }

        return validOptions;
    };

    const status = React.useMemo(() => {
        if (NutClientConfig.hasDraftPipelinesFF) {
            return (
                getPipelineStatusBadge(props.stageset.status, props.stageset.default, isArchived) ||
                undefined
            );
        } else if (props.stageset.default) {
            return {
                text: 'Default',
                level: 'success',
            };
        } else {
            return undefined;
        }
    }, [props.stageset.default, props.stageset.status, isArchived]);

    const isStagesetDraft =
        props.stageset.status === 'DRAFT' || props.stageset.status === 'RETIRED';

    return (
        <div className={containerStyle}>
            <ActionableHeader
                isArchived={isArchived}
                name={props.stageset.name}
                to={props.stageset.htmlUrlPath}
                metadataPrimary={
                    isArchived ? null : (
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            to={getLeadsListLink(props.stageset.id)}
                            variant='primary'
                        >
                            {`${openLeadCount} ${openLeadText}`}
                        </Link>
                    )
                }
                // @ts-expect-error
                status={status}
                secondaryLink={
                    isArchived || isDefault || isStagesetDraft
                        ? undefined // No secondary link if default, archived, or draft
                        : {
                              onClick: () => {
                                  setIsEditingDistributionRules(true);
                              },
                              text: props.stageset.leadDistributionAutomation
                                  ? 'Edit lead distribution'
                                  : '+ Add lead distribution',
                          }
                }
                onSecondaryLinkClick={() => {
                    setIsEditingDistributionRules(true);
                }}
                options={getActionMenuOptions()}
            />
            {isArchived ? (
                <div className={stagesContainerStyle}>{stageItems}</div>
            ) : (
                // @ts-expect-error
                <RoutedLink to={props.stageset.htmlUrlPath} className={stagesContainerStyle}>
                    {stageItems}
                </RoutedLink>
            )}
            {isAttemptingToArchivePipeline ? (
                // @ts-expect-error
                <ConfirmationDialog
                    title='Are you sure you want to archive this pipeline?'
                    onCancel={onArchiveCancel}
                    onConfirm={onArchiveConfirm}
                />
            ) : undefined}
            {isAttemptingToDeactivatePipeline ? (
                // @ts-expect-error
                <ConfirmationDialog
                    title='Are you sure you want to deactivate this pipeline?'
                    onCancel={onDeactivateCancel}
                    onConfirm={onDeactivateConfirm}
                />
            ) : undefined}
            {displayRenameAlert ? (
                <PipelineNamePopup onClose={handleRenamePopupClose} stageset={props.stageset} />
            ) : undefined}
            {displayDuplicatePopup ? (
                <PipelineDuplicatePopup
                    onClose={handleDuplicatePopupClose}
                    onSuccess={handleDuplicatePopupSuccess}
                    stageset={props.stageset}
                />
            ) : undefined}
            {cannotArchiveMessage ? (
                <ConfirmationDialog
                    title='Oops!'
                    message={cannotArchiveMessage}
                    cancelText='Got it'
                    onCancel={onArchiveAcknowledge}
                />
            ) : undefined}
            {cannotDeactivateMessage ? (
                <ConfirmationDialog
                    title='Oops!'
                    message={cannotDeactivateMessage}
                    cancelText='Got it'
                    onCancel={onDeactivateAcknowledge}
                />
            ) : undefined}
            {isEditingDistributionRules ? (
                <LeadDistributionEditor
                    stagesetId={props.stageset.id}
                    leadDistributionId={
                        props.stageset.leadDistributionAutomation
                            ? props.stageset.leadDistributionAutomation.id
                            : null
                    }
                    onClose={() => setIsEditingDistributionRules(false)}
                />
            ) : undefined}
            {displayActivatePipelineModal && (
                <ActivateDraftPipelineConfirmationModal
                    stagesetId={props.stageset.id}
                    stagesetName={props.stageset.name}
                    onClose={() => setDisplayActivatePipelineModal(false)}
                />
            )}
        </div>
    );
}

const DELETE_STAGESET = gql`
    mutation DeleteStageset($stageset: DeleteStagesetInputFieldInput!) {
        deleteStageset(input: {stageset: $stageset}) {
            stageset {
                ...StagesetBasic
            }
        }
    }
    ${STAGESET_BASIC}
`;

const RESTORE_STAGESET = gql`
    mutation RestoreStageset($stageset: RestoreStagesetInputFieldInput!) {
        restoreStageset(input: {stageset: $stageset}) {
            stageset {
                ...StagesetBasic
            }
        }
    }
    ${STAGESET_BASIC}
`;

class DeleteMutation extends Mutation<DeleteStagesetMutation, DeleteStagesetMutationVariables> {}
class RestoreMutation extends Mutation<RestoreStagesetMutation, RestoreStagesetMutationVariables> {}
class SetDefaultMutation extends Mutation<
    SetDefaultStagesetMutation,
    SetDefaultStagesetMutationVariables
> {}

const PipelineListItemGraphQL = (props: OwnProps & ContextRouter) => {
    return (
        <DeleteMutation mutation={DELETE_STAGESET}>
            {(deleteStageset) => (
                <RestoreMutation mutation={RESTORE_STAGESET}>
                    {(restoreStageset) => (
                        <SetDefaultMutation mutation={SET_DEFAULT_STAGESET}>
                            {(setAsDefault) => (
                                <PipelineListItemComponent
                                    // @ts-expect-error
                                    deleteStageset={(payload) =>
                                        deleteStageset({
                                            variables: {
                                                stageset: {
                                                    id: payload.id,
                                                },
                                            },
                                            update: (proxy) => {
                                                // Read the data from our cache for this query.
                                                const cacheResult:
                                                    | StagesetsForPipelineListQuery
                                                    | null
                                                    | undefined = proxy.readQuery({
                                                    query: GET_STAGESETS,
                                                });

                                                if (!cacheResult) return;

                                                // Remove the deleted stageset from the list of stagesets
                                                const newStagesets = cacheResult.stagesets.map(
                                                    (stageset) => {
                                                        if (stageset.id === payload.id) {
                                                            stageset.isArchived = true;
                                                        }

                                                        return stageset;
                                                    }
                                                );

                                                cacheResult.stagesets = newStagesets;

                                                // Write our data back to the cache.
                                                proxy.writeQuery({
                                                    query: GET_STAGESETS,
                                                    data: cacheResult,
                                                });
                                            },
                                        })
                                    }
                                    // @ts-expect-error
                                    restoreStageset={(payload) =>
                                        restoreStageset({
                                            variables: {
                                                stageset: {
                                                    id: payload.id,
                                                },
                                            },
                                            update: (proxy) => {
                                                // Read the data from our cache for this query.
                                                const cacheResult:
                                                    | StagesetsForPipelineListQuery
                                                    | null
                                                    | undefined = proxy.readQuery({
                                                    query: GET_STAGESETS,
                                                });

                                                if (!cacheResult) return;

                                                // Remove the deleted stageset from the list of stagesets
                                                const newStagesets = cacheResult.stagesets.map(
                                                    (stageset) => {
                                                        if (stageset.id === payload.id) {
                                                            stageset.isArchived = false;
                                                        }

                                                        return stageset;
                                                    }
                                                );

                                                cacheResult.stagesets = newStagesets;

                                                // Write our data back to the cache.
                                                proxy.writeQuery({
                                                    query: GET_STAGESETS,
                                                    data: cacheResult,
                                                });
                                            },
                                        })
                                    }
                                    // @ts-expect-error
                                    setAsDefault={(payload) =>
                                        setAsDefault({
                                            variables: {
                                                stageset: {
                                                    id: payload.id,
                                                },
                                            },
                                            update: (proxy, mutationResult) => {
                                                const newStagesets =
                                                    mutationResult.data &&
                                                    mutationResult.data.setDefaultStageset &&
                                                    mutationResult.data.setDefaultStageset
                                                        .stagesets;
                                                if (!newStagesets) return;

                                                // Read the data from our cache for this query.
                                                const cacheResult:
                                                    | StagesetsForPipelineListQuery
                                                    | null
                                                    | undefined = proxy.readQuery({
                                                    query: GET_STAGESETS,
                                                });

                                                if (!cacheResult) return;

                                                cacheResult.stagesets = newStagesets;

                                                // Write our data back to the cache.
                                                proxy.writeQuery({
                                                    query: GET_STAGESETS,
                                                    data: cacheResult,
                                                });
                                            },
                                        })
                                    }
                                    {...props}
                                />
                            )}
                        </SetDefaultMutation>
                    )}
                </RestoreMutation>
            )}
        </DeleteMutation>
    );
};

// @ts-expect-error
export const PipelineListItem = withRouter<OwnProps>(PipelineListItemGraphQL);
